import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './WrapSubProcessDialog.css';

/**
 * Dialog that lets the user choose a name and type (expanded / collapsed)
 * when wrapping selected elements into a sub-process.
 */
const WrapSubProcessDialog = ({ isOpen, onConfirm, onCancel, elementCount, warnings = [] }) => {
  const [name, setName] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const inputRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setCollapsed(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [onCancel]);

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm({ name: name.trim(), collapsed });
  };

  return (
    <div className="wrap-sp-overlay">
      <button
        type="button"
        className="wrap-sp-overlay-btn"
        onClick={onCancel}
        aria-label="Dialog schließen"
      />
      <dialog className="wrap-sp-dialog" open aria-label="In Sub-Prozess umwandeln">
        <div className="wrap-sp-header">
          <span className="wrap-sp-icon">
            <i className="bi bi-diagram-3-fill"></i>
          </span>
          <h5>In Sub-Prozess umwandeln</h5>
        </div>
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="wrap-sp-body">
            <p className="wrap-sp-info">
              {elementCount} Element{elementCount === 1 ? '' : 'e'} werden in einen neuen Sub-Prozess verschoben.
            </p>

            {warnings.length > 0 && (
              <div className="wrap-sp-warning">
                <div className="wrap-sp-warning-header">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <strong>Konsistenz-Warnung</strong>
                </div>
                <p>
                  Die folgenden Elemente wurden nicht ausgewählt, sind aber
                  ausschließlich mit ausgewählten Elementen verbunden.
                  Nach dem Umwandeln würden sie außerhalb des Sub-Prozesses hängen:
                </p>
                <ul>
                  {warnings.map((w) => (
                    <li key={w.id}>{w.name}</li>
                  ))}
                </ul>
                <p className="wrap-sp-warning-hint">
                  <i className="bi bi-lightbulb"></i>{' '}
                  Erwäge, diese Elemente ebenfalls auszuwählen, bevor du fortfährst.
                </p>
              </div>
            )}

            <label htmlFor="wrap-sp-name">Name (optional)</label>
            <input
              id="wrap-sp-name"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Genehmigungsprozess"
              autoComplete="off"
            />

            <fieldset className="wrap-sp-type-group">
              <legend>Darstellung</legend>
              <label className={`wrap-sp-radio${collapsed ? ' active' : ''}`} aria-label="Zugeklappt">
                <input
                  type="radio"
                  name="subProcessType"
                  checked={collapsed}
                  onChange={() => setCollapsed(true)}
                />
                <span className="wrap-sp-radio-icon">
                  <i className="bpmn-icon-subprocess-collapsed"></i>
                </span>
                <span className="wrap-sp-radio-text">
                  <strong>Zugeklappt</strong>
                  <small>Kompakte Darstellung — Inhalte per Klick erreichbar</small>
                </span>
              </label>
              <label className={`wrap-sp-radio${collapsed ? '' : ' active'}`} aria-label="Aufgeklappt">
                <input
                  type="radio"
                  name="subProcessType"
                  checked={!collapsed}
                  onChange={() => setCollapsed(false)}
                />
                <span className="wrap-sp-radio-icon">
                  <i className="bpmn-icon-subprocess-expanded"></i>
                </span>
                <span className="wrap-sp-radio-text">
                  <strong>Aufgeklappt</strong>
                  <small>Elemente bleiben direkt sichtbar im Rahmen</small>
                </span>
              </label>
            </fieldset>
          </div>
          <div className="wrap-sp-footer">
            <button type="submit" className="btn wrap-sp-btn-create">
              <i className="bi bi-diagram-3"></i> Umwandeln
            </button>
            <button type="button" className="btn wrap-sp-btn-cancel" onClick={onCancel}>
              Abbrechen
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
};

WrapSubProcessDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  elementCount: PropTypes.number,
  warnings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
};

export default WrapSubProcessDialog;
